import { Component } from '../jsti/core';
import Notifications from '../constants/Notifications';
import { clearChildElements } from '../classes/Helpers';
import SearchSuggestionTypes from '../constants/search/SearchSuggestionTypes';
import KeywordSuggestions from '../classes/search/suggestions/KeywordSuggestions';
import BrandSuggestions from '../classes/search/suggestions/BrandSuggestions';
import ContentSuggestions from '../classes/search/suggestions/ContentSuggestions';
import ProductSuggestions from '../classes/search/suggestions/ProductSuggestions';
import Template from '../classes/Template';
import EventNames from '../constants/EventNames';
import Collection from '../classes/Collection';
import type { SearchSuggestion } from '../interfaces/SearchSuggestion';

export default class SearchSuggestions extends Component {
  #productTemplate: HTMLElement;
  #items: Array<SearchSuggestion> = [];
  filledClass: string = 'search__suggestions--filled';
  activeClass = 'bg-light-grey';

  /**
   * Initialize the component.
   *
   * @param pEl
   */
  bind(pEl: HTMLElement) {
    super.bind(pEl);

    this.addListener(Notifications.UPDATE_SEARCH_SUGGESTIONS, this.updateSuggestions);
    this.addListener(Notifications.CLEARED_SEARCH_KEYWORD, this.clearSuggestions);
    this.addListener(Notifications.NEXT_SEARCH_SUGGESTION, this.onNext);
    this.addListener(Notifications.PREVIOUS_SEARCH_SUGGESTION, this.onPrevious);
    this.addEventListener(EventNames.TOUCH_START, this.onTouch.bind(this), { passive: true });

    this.#productTemplate = Template.getContent(this.el.getElementByRef('product-suggestion'));
  }

  /**
   * Build all new suggestions.
   */
  updateSuggestions({ suggestions, dataLayer }: Object): void {
    this.clearSuggestions();

    if (!suggestions) return;
    const productDataLayerData: Collection<Object> = new Collection(dataLayer?.productImpressions?.searchSuggestions || []);
    const contentDataLayerData: Collection<Object> = new Collection(dataLayer?.contentImpressions?.searchSuggestions || []);
    const brandDataLayerData: Collection<Object> = new Collection(dataLayer?.brandImpressions?.searchSuggestions || []);
    const keywordDataLayerData: Collection<Object> = new Collection(dataLayer?.keywordImpressions?.searchSuggestions || []);

    for (const [type, data] of Object.entries(suggestions)) {
      switch (type) {
        case SearchSuggestionTypes.KEYWORDS:
          if (!KeywordSuggestions.hasValidData(data)) continue;
          this.el.appendChild(new KeywordSuggestions(data, keywordDataLayerData).build());
          break;
        case SearchSuggestionTypes.PRODUCTS:
          if (!ProductSuggestions.hasValidData(data)) continue;
          this.el.appendChild(new ProductSuggestions(data, productDataLayerData, this.#productTemplate).build());
          break;
        case SearchSuggestionTypes.BRANDS:
          if (!BrandSuggestions.hasValidData(data)) continue;
          this.el.appendChild(new BrandSuggestions(data, brandDataLayerData).build());
          break;
        case SearchSuggestionTypes.CONTENT:
          if (!ContentSuggestions.hasValidData(data)) continue;
          this.el.appendChild(new ContentSuggestions(data.items, data.type, contentDataLayerData).build());
          break;
      }

      if (!this.el.classList.contains(this.filledClass)) {
        this.el.classList.add(this.filledClass);
      }
    }

    this.#items = [...this.el.getElementsByRef('suggestion')].map((element: HTMLElement) => {
      return {
        element,
        active: false,
        name: (element.getElementByRef('title')?.innerText || element.innerHTML).trim(),
        url: element.getAttribute('href')
      };
    });
  }

  /**
   * Clear the suggestions
   */
  clearSuggestions(): void {
    this.#items = [];
    clearChildElements(this.el);
    this.el.classList.remove(this.filledClass);
  }

  /**
   * notifies a touch on the search suggestions
   */
  onTouch(): void {
    this.notify(Notifications.SEARCH_SUGGESTION_TOUCH);
  }

  /**
   * Executes when the component receives a notification
   * Sets the next search suggestion as active
   */
  onNext(): void {
    const oldActiveItem = this.activeItem;

    if (!oldActiveItem) {
      this.activate(this.#items[0]);
    } else {
      this.deactivate(oldActiveItem);

      const lastItem = this.#items[this.#items.length - 1];
      if (oldActiveItem !== lastItem) {
        this.activate(this.#items[this.#items.indexOf(oldActiveItem) + 1]);
      }
    }

    if (!this.activeItem || this.#items[0] === this.activeItem) {
      this.scrollToTop();
    } else {
      this.scrollTo(this.activeItem);
    }

    this.notify(Notifications.SEARCH_SUGGESTION_CHOSEN, { suggestion: this.activeItem });
  }

  /**
   * Executes when the component receives a notification
   * Sets the previous search suggestion as active
   */
  onPrevious(): void {
    const oldActiveItem = this.activeItem;

    if (!oldActiveItem) {
      this.activate(this.#items[this.#items.length - 1]);
    } else {
      this.deactivate(oldActiveItem);

      if (this.#items[0] !== oldActiveItem) {
        this.activate(this.#items[this.#items.indexOf(oldActiveItem) - 1]);
      }
    }

    if (!this.activeItem || this.#items[0] === this.activeItem) {
      this.scrollToTop();
    } else {
      this.scrollTo(this.activeItem);
    }

    this.notify(Notifications.SEARCH_SUGGESTION_CHOSEN, { suggestion: this.activeItem });
  }

  /**
   * Activate the given suggestion
   */
  activate(suggestion: SearchSuggestion): void {
    suggestion.active = true;
    suggestion.element.classList.add(this.activeClass);
  }

  /**
   * Deactivate the given suggestion
   */
  deactivate(suggestion: SearchSuggestion): void {
    suggestion.active = false;
    suggestion.element.classList.remove(this.activeClass);
  }

  /**
   * Scroll to the given suggestion
   */
  scrollTo(suggestion: SearchSuggestion): void {
    suggestion.element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  /**
   * Scroll to the top
   */
  scrollToTop(): void {
    this.el.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  /**
   * Get the active item
   */
  get activeItem(): ?SearchSuggestion {
    return this.#items.find((item: SearchSuggestion) => !!item.active) || null;
  }
}
